import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { enUS, pl } from 'date-fns/locale';
import type { Locale } from 'date-fns';
import en from './en.json';
import plMessages from './pl.json';

export const SUPPORTED_LANGUAGES = ['en', 'pl'] as const;
export type Language = (typeof SUPPORTED_LANGUAGES)[number];

/**
 * What a user can *pick* in the UI. 'system' means "no explicit override —
 * follow navigator.language", and stores literally as the string 'system'
 * (parallels theme's 'system' value).
 */
export const LANGUAGE_PREFERENCES = ['en', 'pl', 'system'] as const;
export type LanguagePreference = (typeof LANGUAGE_PREFERENCES)[number];

export const LANGUAGE_STORAGE_KEY = 'kstefan.lang';

/**
 * Pure function so tests can drive it without stubbing globals.
 * Returns the runtime language ('en' | 'pl'). 'system' and any unknown
 * stored value fall through to the browser hint.
 */
export function detectInitialLanguage(stored: string | null, browser: string): Language {
  if (stored === 'en' || stored === 'pl') return stored;
  return browser.toLowerCase().startsWith('pl') ? 'pl' : 'en';
}

function detectFromEnvironment(): Language {
  const stored = typeof window !== 'undefined' ? window.localStorage.getItem(LANGUAGE_STORAGE_KEY) : null;
  const browser = typeof navigator !== 'undefined' ? navigator.language : '';
  return detectInitialLanguage(stored, browser);
}

/** Returns the user's stored language preference, defaulting to 'system'. */
export function getLanguagePreference(): LanguagePreference {
  if (typeof window === 'undefined') return 'system';
  const raw = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
  return raw === 'en' || raw === 'pl' ? raw : 'system';
}

/**
 * Apply a user-chosen language preference: persist it to localStorage and
 * switch i18next to the resolved runtime language. 'system' resolves against
 * navigator.language; on reload the same detection runs.
 *
 * The `languageChanged` listener below will overwrite localStorage with the
 * runtime value ('en' or 'pl'); we then re-write 'system' so subsequent reads
 * see the preference, not the resolved value.
 */
export async function applyLanguagePreference(pref: LanguagePreference): Promise<void> {
  const resolved = pref === 'system'
    ? detectInitialLanguage(null, typeof navigator !== 'undefined' ? navigator.language : '')
    : pref;
  await i18n.changeLanguage(resolved);
  if (typeof window !== 'undefined' && pref === 'system') {
    window.localStorage.setItem(LANGUAGE_STORAGE_KEY, 'system');
  }
}

void i18n.use(initReactI18next).init({
  resources: {
    en: { translation: en },
    pl: { translation: plMessages },
  },
  lng: detectFromEnvironment(),
  fallbackLng: 'en',
  interpolation: { escapeValue: false },
  returnNull: false,
});

i18n.on('languageChanged', (lng) => {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(LANGUAGE_STORAGE_KEY, lng);
  document.documentElement.lang = lng;
});

if (typeof document !== 'undefined') {
  document.documentElement.lang = i18n.language;
}

const DATE_LOCALES: Record<Language, Locale> = { en: enUS, pl };
export const dateLocaleFor = (lng: string): Locale => DATE_LOCALES[lng as Language] ?? enUS;

export default i18n;
